// ============================================================================
// Project workflow — pure decision helpers (v5.41.39)
// ============================================================================
// Single source of truth for the Fast-Track-vs-Full-AI routing decision so it
// can be headless-tested against the SAME code App.jsx runs — no spec-mirror.
//
// Before this module the routing lived inline in App.jsx's handleNext and the
// test (fast-track-manual-scope.test.js) re-implemented a COPY of it, which
// could silently drift from the real code. App.jsx now imports
// stage1SummaryContinuation() and acts on its result; the test exercises the
// very same function.
// ============================================================================

export const PROJECT_WORKFLOW_MODES = Object.freeze({
    FULL_AI: 'full-ai-assisted',
    FAST_TRACK_MANUAL_SCOPE: 'fast-track-manual-scope',
});

/** True when the project is in the Fast-Track (manual scope) workflow. */
export function isFastTrackManualScope(workflowMode: string): boolean {
    return workflowMode === PROJECT_WORKFLOW_MODES.FAST_TRACK_MANUAL_SCOPE;
}

export interface Stage1Continuation {
    nextStage: number;
    target: string;
    skipsRiskAnalysis: boolean;
    skipsWorkMethod: boolean;
    runsRiskAnalysis: boolean;
}

/**
 * At the Stage 1 Project Summary, clicking the forward button opens the Scope
 * Builder (Stage 4). The Risk report (Stage 2) and the Work Method (Stage 3)
 * went with the old AI pipeline (Peter, 2026-11-01,
 * docs/old-pipeline-removal/PLAN.md): QuoteMaker writes a job's quote on the
 * server, and a builder who goes on to the Scope Builder writes it by hand.
 * The mode is kept as an argument so a caller reads one decision for both.
 */
export function stage1SummaryContinuation(_workflowMode?: string): Readonly<Stage1Continuation> {
    return Object.freeze({
        nextStage: 4,
        target: 'scope-builder',
        skipsRiskAnalysis: true,
        skipsWorkMethod: true,
        runsRiskAnalysis: false,
    });
}

/**
 * Normalise a persisted / user-supplied workflow mode to a known enum value.
 * Anything that isn't the Fast-Track sentinel resolves to Full-AI.
 *
 * Relocated verbatim from App.jsx (TS-060 / DEC-COUNCIL-015 T1) — this module
 * is the canonical home for the workflow-mode enum + its helpers.
 */
export const normalizeProjectWorkflowMode = (value?: string | null): string => (
    value === PROJECT_WORKFLOW_MODES.FAST_TRACK_MANUAL_SCOPE
        ? PROJECT_WORKFLOW_MODES.FAST_TRACK_MANUAL_SCOPE
        : PROJECT_WORKFLOW_MODES.FULL_AI
);

export interface WorkflowModeToast {
    type: 'success' | 'info';
    duration: number;
    message: string;
}

/** Hover help for "Write it yourself" and its way back, on the Quote Brain bar and the import strip (Peter, 2026-11-01). */
export const WRITE_IT_YOURSELF_TIP = 'Write this job\'s scope yourself in the Scope Builder, and QuoteMaker will not write it. Until you add items, you can hand it back.';
export const HAND_BACK_TIP = 'Hand this job back to QuoteMaker. It writes the quote from the job\'s files by itself, and you can close the tab while it works.';

// v5.41.38: the workflow choice used to apply silently - both modes landed on
// the same Import screen, so the user couldn't tell their pick took effect.
// This toast fires the moment the mode is applied (and on an in-place switch),
// spelling out exactly what each mode does.
// Under the Quote Brain the choice is who writes the job's quote (Peter's O1, 2026-10-31), so it says that instead.
export const workflowModeToast = (mode: string, { quoteBrain = false }: { quoteBrain?: boolean } = {}): WorkflowModeToast => {
    const manual = mode === PROJECT_WORKFLOW_MODES.FAST_TRACK_MANUAL_SCOPE;
    if (quoteBrain) {
        return manual
            ? { type: 'success', duration: 6000, message: 'You write this job\'s scope yourself, so QuoteMaker does not start on it. Continue to the Scope Builder once the summary is written.' }
            : { type: 'success', duration: 6000, message: 'QuoteMaker writes this job\'s quote by itself once its files are read.' };
    }
    return manual
        ? { type: 'success', duration: 6000, message: 'Fast-Track workflow set. Once the summary in the Job cockpit is written you skip Risk and Work Method and go straight to the Scope Builder.' }
        : { type: 'info', duration: 6000, message: 'Full AI-Assisted workflow set — Risk analysis → Work Method → costed Scope → final Quote.' };
};
